import React, { useState, useEffect } from "react";
import { Tabs, Typography, message } from "antd";
import { supabase } from "../lib/supabase";
import { trackFeatureUsage, captureError } from "../lib/posthog";
import OriginPortsTab from "../components/ports/OriginPortsTab";
import DestinationPortsTab from "../components/ports/DestinationPortsTab";
import { AdminNavBar } from "../components/AdminNavBar";

const { Title } = Typography;

// Port type definition
export interface Port {
  id: number;
  name: string;
  country: string;
  created_at: string;
  latitude?: number | null;
  longitude?: number | null;
}

const PortsAdmin = () => {
  const [activeTab, setActiveTab] = useState("origin");
  const [originPorts, setOriginPorts] = useState<Port[]>([]);
  const [destinationPorts, setDestinationPorts] = useState<Port[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch origin ports from Supabase
  const fetchOriginPorts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("booking_origin_port")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      setOriginPorts(data || []);
    } catch (error) {
      console.error("Error fetching origin ports:", error);
      message.error("Failed to load origin ports");
      captureError(error, {
        source: "PortsAdmin",
        action: "fetchOriginPorts",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch destination ports from Supabase
  const fetchDestinationPorts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("booking_destination_port")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      setDestinationPorts(data || []);
    } catch (error) {
      console.error("Error fetching destination ports:", error);
      message.error("Failed to load destination ports");
      captureError(error, {
        source: "PortsAdmin",
        action: "fetchDestinationPorts",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Track page view
    trackFeatureUsage("ports_admin");

    fetchOriginPorts();
    fetchDestinationPorts();

    return () => {
      // Cleanup if needed
    };
  }, []);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const items = [
    {
      key: "origin",
      label: "Origin Ports",
      children: (
        <OriginPortsTab
          ports={originPorts}
          loading={loading}
          refreshPorts={fetchOriginPorts}
        />
      ),
    },
    {
      key: "destination",
      label: "Destination Ports",
      children: (
        <DestinationPortsTab
          ports={destinationPorts}
          loading={loading}
          refreshPorts={fetchDestinationPorts}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />

      <Title level={2}>Ports Administration</Title>
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={items} />
    </div>
  );
};

export default PortsAdmin;
